import {
  JourneyGetPayloadInterface,
  JourneyGetResponseInterface,
  JourneysItemInterface,
  WaypointsUpdatePayload,
  IEvaluatingStatus,
  CancelJourneyPayloadInterface,
  JourneyCreatePayloadInterface,
  JourneyStatusType,
  JourneyGetResponseMonitoringInterface,
  JourneyCheckUpdatesResponseInterface,
} from '@interface/journeys';
import { PER_PAGE } from '@constants';
import { getTimestamp } from '@utils/parsers/stringToDate';
import { JourneyWaitingConfirmDeliveryInterface } from '@context/reducers';
import { HistoryGetResponseInterface } from '@interface/journeyHistory';
import debounceFunctions from '@utils/debounceFunctions';
import apiService from './config';

interface CheckJourneyPayload {
  freightId: string;
  truckerId: string;
}

export default function getJourneys({
  page = 1,
  filters,
}: JourneyGetPayloadInterface): Promise<JourneyGetResponseInterface> {
  let status;

  if (filters?.status) {
    status = filters.status.join(',');
  }

  const params = {
    'filters[status]': status,
    page,
    tab: 'journeys',
    orderBy: 'created_at',
    perPage: PER_PAGE,
  };

  if (filters?.search) {
    params['filters[search]'] = filters.search;
  }

  if (filters?.hasGr) {
    params['filters[has_gr]'] = 'true';
  }

  return apiService
    .get<JourneyGetResponseInterface>(`v2/journeys/`, {
      params,
    })
    .then((res) => res) as Promise<JourneyGetResponseInterface>;
}

export function getJourneyByCode(
  journeyCode: string,
): Promise<JourneysItemInterface> {
  const params = {
    'filters[search]': journeyCode,
  };

  return apiService
    .get<JourneysItemInterface[]>('journeys/', { params })
    .then((res) => {
      if (res.data.length) return res.data[0];
      throw new Error('Sem dados');
    });
}

export function getByStatus(
  status: JourneyStatusType,
): Promise<JourneyWaitingConfirmDeliveryInterface[]> {
  const params = {
    page: 1,
    order_by: 'created_at',
    per_page: PER_PAGE,
  };

  return apiService
    .get<JourneyWaitingConfirmDeliveryInterface[]>(
      `journeys/status/${status}`,
      {
        params,
      },
    )
    .then((res) => res.data) as Promise<
    JourneyWaitingConfirmDeliveryInterface[]
  >;
}

export const finishJourney = (
  journey_id: string,
): Promise<IEvaluatingStatus> => {
  const uri = `journeys/${journey_id}/finish`;

  return apiService.patch(uri).then((response) => response.data);
};

export const unrecognizedJourney = ({
  journeyId,
  payload,
}: {
  journeyId: string;
  payload: {
    reason: string;
  };
}): Promise<IEvaluatingStatus> => {
  const uri = `journeys/${journeyId}/unrecognized`;

  return apiService.patch(uri, payload);
};

export const checkJourneyInProgress = (payload: CheckJourneyPayload) => {
  const uri = `journeys/freights/${payload.freightId}/truckers/${payload.truckerId}/journey`;

  return apiService.get(uri).then((response) => response.data);
};

export const updateWaypoints = (
  journeyId: string | null,
  payload: WaypointsUpdatePayload,
) => {
  const uri = `journeys/${journeyId}/waypoints`;

  return apiService.patch(uri, payload);
};

export const cancelJourney = ({
  id,
  payload,
}: {
  id: string;
  payload: CancelJourneyPayloadInterface;
}) => {
  const uri = `journeys/${id}/status/canceled`;

  return apiService.patch(uri, payload);
};

const parsePaylodCreatedJourney = (payload: JourneyCreatePayloadInterface) => {
  const tempPayload = { ...payload };

  if (payload.waypoints.pickup.date) {
    tempPayload.waypoints.pickup.date = getTimestamp(
      payload.waypoints.pickup.date,
    ).toString();
  }

  if (payload.waypoints.delivery.date) {
    tempPayload.waypoints.delivery.date = getTimestamp(
      payload.waypoints.delivery.date,
    ).toString();
  }

  return tempPayload;
};

export const createJourney = (
  payload: JourneyCreatePayloadInterface,
): Promise<void> => {
  const uri = `journeys/create`;

  return apiService.post(uri, parsePaylodCreatedJourney(payload));
};

export const occurrenceRead = (id: string): Promise<void> => {
  const uri = `journeys/occurrence/${id}/read`;

  return apiService.patch(uri);
};

export function getJourneyById(
  journeyID: JourneyGetResponseMonitoringInterface['data']['journey']['id'],
): Promise<JourneyGetResponseMonitoringInterface['data']> {
  return apiService
    .get<JourneyGetResponseMonitoringInterface['data']>(
      `v3/journeys/${journeyID}`,
    )
    .then((response) => {
      if (response.data) return response.data;
      throw new Error('Sem dados');
    });
}

export function getEventsHistory(
  journeyId: string,
): Promise<HistoryGetResponseInterface> {
  return apiService.get(`journeys/${journeyId}/event-history`);
}

export function checkUpdates(
  lastUpdate: string,
): Promise<JourneyCheckUpdatesResponseInterface['data']> {
  return apiService
    .get(`v2/journeys/check-updates`, {
      params: { lastUpdate },
    })
    .then((response) => response.data);
}

export const getJourneysDebounced = debounceFunctions(getJourneys, 500);
export const checkUpdatesDebounced = debounceFunctions(checkUpdates, 500);
